import type {
	SmartField_Api_Features_DataWarehouse_DataExport_SiteHierarchyDto as SiteHierarchyDto,
	SmartField_Api_Features_DataWarehouse_DataExport_HierarchyFieldDto as HierarchyFieldDto,
	SmartField_Api_Features_DataWarehouse_DataExport_HierarchyTreatmentDto as HierarchyTreatmentDto,
	SmartField_Api_Features_DataWarehouse_DataExport_HierarchyPlotDto as HierarchyPlotDto,
	SmartField_Api_Features_DataWarehouse_DataExport_HierarchySensorDto as HierarchySensorDto,
} from '../../../api';

// Map sensorType strings to group labels
const SENSOR_GROUPS: Record<string, string> = {
	AirTemperature: 'Atmospheric', RelativeAirHumidity: 'Atmospheric', AirPressure: 'Atmospheric', SonicTemperature: 'Atmospheric',
	SoilTemperature: 'Soil', SoilMoisture: 'Soil', VolumetricWaterContent: 'Soil', MatricPotential: 'Soil', SoilAbsolutePressure: 'Soil', NeutronCount: 'Soil', SaturationExtractEC: 'Soil',
	NitricOxide: 'Gas', NitrogenDioxide: 'Gas', CarbonDioxide: 'Gas', NitrousOxide: 'Gas', Ammonia: 'Gas', WaterVapor: 'Gas', Ozone: 'Gas', Methane: 'Gas', Ethane: 'Gas',
	N2OIsotopologue446: 'Isotope', N2OIsotopomer456: 'Isotope', N2OIsotopomer546: 'Isotope', N2OIsotopologue448: 'Isotope',
	Precipitation: 'Meteo', WindSpeed: 'Meteo', WindGust: 'Meteo', WindVector3D: 'Meteo', WindDirection: 'Meteo', VaporPressure: 'Meteo', VaporPressureDeficit: 'Meteo',
	SolarRadiation: 'Radiation', EmittedLongWave: 'Radiation', IncidentLongWave: 'Radiation', IncidentShortWave: 'Radiation', NetRadiation: 'Radiation', ReflectedShortWave: 'Radiation',
	CropTargetHeight: 'Crop', CropMaxHeight: 'Crop', CropMinHeight: 'Crop', CropMeanHeight: 'Crop', CropHeightStdDev: 'Crop',
};

function makeTreatmentKey(fieldId: number, desc: string, plots: { locationId?: number | null }[]) {
	const plotIds = plots.map(p => p.locationId ?? 0).sort((a, b) => a - b).join(',');
	return `${fieldId}|${desc}|${plotIds}`;
}

function sensorGroups(sensors: HierarchySensorDto[]): string[] {
	const groups = new Set<string>();
	for (const s of sensors) {
		if (s.sensorType) {
			groups.add(SENSOR_GROUPS[s.sensorType] ?? s.sensorType);
		}
	}
	return Array.from(groups);
}

type ExportHierarchyPanelProps = {
	hierarchy: SiteHierarchyDto | undefined;
	selectedFields: Set<number>;
	selectedLocationIds: Set<number>;
	selectedSensorIds: Set<number>;
	openFields: Set<number>;
	openTreatments: Set<string>;
	dataTypes: Set<string>;
	onFieldToggle: (fieldId: number) => void;
	onFieldSelect: (fieldId: number) => void;
	onTreatmentToggle: (treatmentKey: string, fieldId: number) => void;
	onTreatmentExpandToggle: (treatmentKey: string) => void;
	onPlotToggle: (plotId: number) => void;
	onClearSelection: () => void;
	onSelectAll: () => void;
	onSiteToggle: () => void;
	treatmentPlotMap: Map<string, number[]>;
	fieldPlotMap: Map<number, number[]>;
	totalPlotCount: number;
	chamberMode: boolean;
	onChamberModeChange: ((active: boolean) => void) | undefined;
	includeSiteSensors: boolean;
	onSiteSensorsToggle: (include: boolean) => void;
};

function ChevronIcon({ open }: { open: boolean }) {
	return (
		<svg
			className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
		>
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
		</svg>
	);
}

function CapabilityBadge({ label, color }: { label: string; color: string }) {
	return (
		<span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${color}`}>
			{label}
		</span>
	);
}

function SensorGroupBadges({ sensors }: { sensors: HierarchySensorDto[] }) {
	if (!sensors || sensors.length === 0) {
		return null;
	}
	const groups = sensorGroups(sensors);
	if (groups.length === 0) {
		return null;
	}
	return (
		<span className="flex gap-1 flex-wrap">
			{groups.map(g => (
				<span key={g} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
					{g}
				</span>
			))}
		</span>
	);
}

function PlotRow({
	plot,
	selected,
	onClick,
}: {
	plot: HierarchyPlotDto;
	selected: boolean;
	onClick: () => void;
}) {
	const sensors = plot.sensors ?? [];
	return (
		<div
			role="row"
			onClick={onClick}
			className={`w-full cursor-pointer px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-2 ${
				selected
					? 'bg-teal-50 text-teal-800 font-medium border-l-2 border-teal-500'
					: 'text-gray-600 hover:bg-gray-50 border-l-2 border-transparent'
			}`}
		>
			<input
				type="checkbox"
				checked={selected}
				onChange={onClick}
				onClick={e => e.stopPropagation()}
				className="accent-smartfield w-3.5 h-3.5 flex-shrink-0"
			/>
			<span className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
			{plot.plotName}
			<span className="flex gap-1 ml-auto flex-shrink-0 flex-wrap justify-end">
				{plot.chamberNumbers && plot.chamberNumbers.length > 0
					? plot.chamberNumbers.map((n, i) => <CapabilityBadge key={`ch-${n}-${i}`} label={`Chamber ${n}`} color="bg-green-100 text-green-700" />)
					: plot.hasChamberFlux && <CapabilityBadge label="Chamber" color="bg-green-100 text-green-700" />}
				{plot.towerNames && plot.towerNames.length > 0
					? plot.towerNames.map((name, i) => <CapabilityBadge key={`tw-${name}-${i}`} label={name} color="bg-blue-100 text-blue-700" />)
					: plot.hasGradientFlux && <CapabilityBadge label="Tower" color="bg-blue-100 text-blue-700" />}
				{sensors.length > 0 && <SensorGroupBadges sensors={sensors} />}
			</span>
		</div>
	);
}

function TreatmentRow({
	treatment,
	fieldId,
	open,
	selectedLocationIds,
	treatmentPlotMap,
	dataTypes,
	onToggle,
	onExpandToggle,
	onPlotToggle,
}: {
	treatment: HierarchyTreatmentDto;
	fieldId: number;
	open: boolean;
	selectedLocationIds: Set<number>;
	treatmentPlotMap: Map<string, number[]>;
	dataTypes: Set<string>;
	onToggle: (key: string, fieldId: number) => void;
	onExpandToggle: (key: string) => void;
	onPlotToggle: (plotId: number) => void;
}) {
	const desc = treatment.treatmentDescription ?? '';
	const plots = treatment.plots ?? [];
	const key = makeTreatmentKey(fieldId, desc, plots);
	const plotIds = treatmentPlotMap.get(key) ?? [];
	const selectedPlotCount = plotIds.filter(id => selectedLocationIds.has(id)).length;
	const allSelected = plotIds.length > 0 && selectedPlotCount === plotIds.length;
	const someSelected = selectedPlotCount > 0 && !allSelected;

	// Capability of this treatment (union of all plots)
	const hasChamber = plots.some(p => p.hasChamberFlux);
	const hasTower = plots.some(p => p.hasGradientFlux);
	const hasSensors = plots.some(p => (p.sensors ?? []).length > 0);
	const hasManual = plots.some(p => p.hasManualObservations);

	// Grey out if selected data types don't match any capability
	const matchesDataTypes = (
		(dataTypes.has('ChamberFlux') && hasChamber) ||
		(dataTypes.has('GradientFlux') && hasTower) ||
		(dataTypes.has('Sensor') && hasSensors) ||
		(dataTypes.has('Manual') && hasManual)
	);
	const dimmed = dataTypes.size > 0 && !matchesDataTypes;

	// Sensor group summary across all plots
	const allSensors = plots.flatMap(p => p.sensors ?? []);
	const groups = sensorGroups(allSensors);

	return (
		<div className={dimmed ? 'opacity-40 pointer-events-none' : ''}>
			<div className="flex items-center gap-1">
				<button onClick={() => onExpandToggle(key)} className="p-0.5 rounded hover:bg-gray-100">
					<ChevronIcon open={open} />
				</button>
				<div
					role="row"
					onClick={() => onToggle(key, fieldId)}
					className={`flex-1 cursor-pointer px-2 py-1.5 text-sm rounded transition-colors ${
						allSelected
							? 'bg-teal-50 text-teal-800 font-medium border-l-2 border-teal-500'
							: someSelected
							? 'bg-teal-50/50 text-teal-700 border-l-2 border-teal-300'
							: 'text-gray-700 hover:bg-gray-50 border-l-2 border-transparent'
					}`}
				>
					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							checked={allSelected}
							ref={el => { if (el) { el.indeterminate = someSelected; } }}
							onChange={() => onToggle(key, fieldId)}
							onClick={e => e.stopPropagation()}
							className="accent-smartfield w-3.5 h-3.5 flex-shrink-0"
						/>
						<span>{treatment.treatmentFriendlyName ?? `Treatment ${desc || '?'}`}</span>
						<span className="text-xs text-gray-400 font-normal">
							{selectedPlotCount > 0 ? `${selectedPlotCount}/` : ''}{plots.length} plots
						</span>
						<span className="flex gap-1 ml-auto flex-shrink-0">
							{hasChamber && <CapabilityBadge label="Chamber" color="bg-green-100 text-green-700" />}
							{hasTower && <CapabilityBadge label="Tower" color="bg-blue-100 text-blue-700" />}
						</span>
					</div>
					{groups.length > 0 && (
						<div className="ml-6 mt-0.5 flex flex-wrap gap-1">
							{groups.map(g => (
								<span key={g} className="text-xs text-gray-400">{g}</span>
							))}
						</div>
					)}
				</div>
			</div>
			{open && (
				<div className="ml-6 mt-1 space-y-0.5 border-l border-gray-100 pl-2">
					{plots.map(plot => plot.locationId != null && (
						<PlotRow
							key={plot.locationId}
							plot={plot}
							selected={selectedLocationIds.has(plot.locationId)}
							onClick={() => onPlotToggle(plot.locationId!)}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function FieldRow({
	field,
	open,
	selectedLocationIds,
	openTreatments,
	treatmentPlotMap,
	fieldPlotMap,
	dataTypes,
	onToggle,
	onSelect,
	onTreatmentToggle,
	onTreatmentExpandToggle,
	onPlotToggle,
}: {
	field: HierarchyFieldDto;
	open: boolean;
	selectedLocationIds: Set<number>;
	openTreatments: Set<string>;
	treatmentPlotMap: Map<string, number[]>;
	fieldPlotMap: Map<number, number[]>;
	dataTypes: Set<string>;
	onToggle: () => void;
	onSelect: (fieldId: number) => void;
	onTreatmentToggle: (key: string, fieldId: number) => void;
	onTreatmentExpandToggle: (key: string) => void;
	onPlotToggle: (plotId: number) => void;
}) {
	const fid = field.fieldId;
	if (fid == null) {
		return null;
	}

	const treatments = field.treatments ?? [];
	const fieldSensors = field.sensors ?? [];
	const fieldPlotIds = fieldPlotMap.get(fid) ?? [];
	const selectedPlotCount = fieldPlotIds.filter(id => selectedLocationIds.has(id)).length;
	const allFieldSelected = fieldPlotIds.length > 0 && selectedPlotCount === fieldPlotIds.length;
	const someFieldSelected = selectedPlotCount > 0 && !allFieldSelected;

	return (
		<div>
			<div className="flex items-center gap-1">
				<button onClick={onToggle} className="p-0.5 rounded hover:bg-gray-100">
					<ChevronIcon open={open} />
				</button>
				<button
					onClick={() => onSelect(fid)}
					className={`flex-1 text-left px-2 py-2 text-sm font-medium rounded transition-colors ${
						allFieldSelected
							? 'bg-teal-50 text-teal-800 border-l-2 border-teal-500'
							: someFieldSelected
							? 'bg-teal-50/50 text-teal-700 border-l-2 border-teal-300'
							: 'text-gray-800 hover:bg-gray-50 border-l-2 border-transparent'
					}`}
				>
					<span className="flex items-center gap-2">
						<input
							type="checkbox"
							checked={allFieldSelected}
							ref={el => { if (el) { el.indeterminate = someFieldSelected; } }}
							onChange={() => onSelect(fid)}
							onClick={e => e.stopPropagation()}
							className="accent-smartfield w-3.5 h-3.5 flex-shrink-0"
						/>
						{field.fieldName}
						<span className="text-xs text-gray-400 font-normal">{treatments.length} treatments</span>
					</span>
				</button>
			</div>
			{fieldSensors.length > 0 && (
				<div className="ml-8 mb-1">
					<SensorGroupBadges sensors={fieldSensors} />
				</div>
			)}
			{open && (
				<div className="ml-6 mt-1 space-y-1 border-l border-gray-200 pl-2">
					{treatments.map(treatment => {
						const desc = treatment.treatmentDescription ?? '';
						const compositeKey = makeTreatmentKey(fid, desc, treatment.plots ?? []);
						return (
							<TreatmentRow
								key={compositeKey}
								treatment={treatment}
								fieldId={fid}
								open={openTreatments.has(compositeKey)}
								selectedLocationIds={selectedLocationIds}
								treatmentPlotMap={treatmentPlotMap}
								dataTypes={dataTypes}
								onToggle={onTreatmentToggle}
								onExpandToggle={onTreatmentExpandToggle}
								onPlotToggle={onPlotToggle}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

export function ExportHierarchyPanel({
	hierarchy,
	selectedLocationIds,
	selectedSensorIds,
	openFields,
	openTreatments,
	dataTypes,
	onFieldToggle,
	onFieldSelect,
	onTreatmentToggle,
	onTreatmentExpandToggle,
	onPlotToggle,
	onClearSelection,
	onSiteToggle,
	treatmentPlotMap,
	fieldPlotMap,
	totalPlotCount,
	chamberMode,
	onChamberModeChange,
	includeSiteSensors,
	onSiteSensorsToggle,
}: ExportHierarchyPanelProps) {
	if (!hierarchy) {
		return (
			<div className="bg-white rounded-lg shadow p-6 text-center text-gray-400 text-sm">
				Select a site on the map or from the filter panel to explore its hierarchy.
			</div>
		);
	}

	const fields = hierarchy.fields ?? [];
	const siteSensors = hierarchy.siteSensors ?? [];
	const hasSiteSensors = siteSensors.length > 0;
	const totalSelectedPlots = selectedLocationIds.size;
	const totalSelectedSensors = selectedSensorIds.size;
	const hasSelection = totalSelectedPlots > 0 || totalSelectedSensors > 0;
	const allSiteSelected = totalPlotCount > 0 && totalSelectedPlots === totalPlotCount;
	const someSiteSelected = totalSelectedPlots > 0 && !allSiteSelected;

	return (
		<div className="bg-white rounded-lg shadow p-4">
			<div className="flex items-center gap-2 mb-3">
				<h3 className="font-semibold text-gray-900 text-sm">Site hierarchy</h3>
				{!chamberMode && totalSelectedPlots > 0 && (
					<span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
						{totalSelectedPlots} plot{totalSelectedPlots !== 1 ? 's' : ''}
					</span>
				)}
				{!chamberMode && totalSelectedSensors > 0 && (
					<span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
						{totalSelectedSensors} sensor{totalSelectedSensors !== 1 ? 's' : ''}
					</span>
				)}
				{onChamberModeChange && (
					<label className="flex items-center gap-1.5 ml-auto cursor-pointer select-none" title="Select chambers by number instead of using the hierarchy">
						<input
							type="checkbox"
							checked={chamberMode}
							onChange={e => onChamberModeChange(e.target.checked)}
							className="accent-green-600 w-3.5 h-3.5"
						/>
						<span className="text-xs text-gray-600">Select by chamber number</span>
					</label>
				)}
				{!chamberMode && hasSelection && (
					<button
						onClick={onClearSelection}
						className={`text-xs text-gray-400 hover:text-gray-600 ${onChamberModeChange ? '' : 'ml-auto'}`}
					>
						Clear
					</button>
				)}
			</div>

			{chamberMode ? (
				<p className="text-xs text-gray-400 py-4 text-center">Enter chamber numbers in the export panel to select chambers.</p>
			) : (
				<>
					{/* Site-level row */}
					<div
						role="row"
						onClick={onSiteToggle}
						className={`flex items-center gap-2 cursor-pointer px-2 py-2 text-sm font-semibold rounded transition-colors mb-2 ${
							allSiteSelected
								? 'bg-teal-50 text-teal-800 border-l-2 border-teal-500'
								: someSiteSelected
								? 'bg-teal-50/50 text-teal-700 border-l-2 border-teal-300'
								: 'text-gray-900 hover:bg-gray-50 border-l-2 border-transparent'
						}`}
					>
						<input
							type="checkbox"
							checked={allSiteSelected}
							ref={el => { if (el) { el.indeterminate = someSiteSelected; } }}
							onChange={onSiteToggle}
							onClick={e => e.stopPropagation()}
							className="accent-smartfield w-3.5 h-3.5 flex-shrink-0"
						/>
						<span className="w-3 h-3 rounded-sm bg-teal-500 flex-shrink-0" />
						{hierarchy.displayName}
						<span className="text-xs text-gray-400 font-normal ml-1">{fields.length} field{fields.length !== 1 ? 's' : ''}</span>
					</div>

					<div className="ml-4 space-y-1 border-l border-gray-200 pl-2 max-h-[32rem] overflow-y-auto">
						{fields.map(field => (
							<FieldRow
								key={field.fieldId}
								field={field}
								open={openFields.has(field.fieldId!)}
								selectedLocationIds={selectedLocationIds}
								openTreatments={openTreatments}
								treatmentPlotMap={treatmentPlotMap}
								fieldPlotMap={fieldPlotMap}
								dataTypes={dataTypes}
								onToggle={() => onFieldToggle(field.fieldId!)}
								onSelect={onFieldSelect}
								onTreatmentToggle={onTreatmentToggle}
								onTreatmentExpandToggle={onTreatmentExpandToggle}
								onPlotToggle={onPlotToggle}
							/>
						))}
					</div>

					{hasSiteSensors && (
						<div className="mt-3 pt-3 border-t border-gray-100">
							<label className="flex items-center gap-2 cursor-pointer select-none group">
								<input
									type="checkbox"
									checked={includeSiteSensors}
									onChange={e => onSiteSensorsToggle(e.target.checked)}
									className="accent-amber-600 w-3.5 h-3.5 flex-shrink-0"
								/>
								<span className="text-xs font-medium text-gray-700 group-hover:text-gray-900">
									Include site sensors
								</span>
								<span className="text-xs text-gray-400">({siteSensors.length})</span>
								<SensorGroupBadges sensors={siteSensors} />
							</label>
							<p className="text-xs text-gray-400 mt-1 ml-5">Weather stations and other sensors assigned to the site (not a specific plot).</p>
						</div>
					)}
				</>
			)}
		</div>
	);
}
